import { useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { StoreState } from "../models/states/StoreState";
import { DefaultFocusStatusModel } from "../models/DefaultFocusStatusModel";
import { setDefaultFocusStatus } from "../redux/actions/index";
import { isGrid, QuestionType } from "../models/enums/QuestionType";
import { checkIfS2LoadedInsideAgent } from "../utils/voxcoAgentHelper";

export function useKeyboardNavigation(
    elementId,
    variable,
    question,
    NavigationClass,
    onSelect,
    isProbQuestion = false,
) {
    const elementReference = useRef(null);
    const defaultFocusStatus = useSelector((state: StoreState) => state.defaultFocusStatus);
    const parent = useSelector((state: StoreState) => state.application.parent);
    const dispatch = useDispatch();

    const navigation = new NavigationClass(defaultFocusStatus, dispatch, question);
    const isHybridGrid =
        question && question.parent && question.parent.type === QuestionType.HybridGrid;
    const rowIndex = isHybridGrid ? question.rowIndex : null;

    function getCurrentElementFromStore(): DefaultFocusStatusModel | undefined {
        return defaultFocusStatus.statuses.find(
            (el) => variable && el.variableId === variable.id && el.elementId === elementId,
        );
    }

    function getQuestionId(): number | null {
        let questionId = null;
        if (question && question.parent && isGrid(question.parent.type)) {
            questionId = question.parent.id;
        } else if (question) {
            questionId = question.id;
        }
        return questionId;
    }

    // Handle focus management
    function handleFocus(shouldAutoFocus: boolean = false) {
        // Guard: ensure variable exists
        if (!variable) {
            return;
        }

        // For AI probe questions, we need the DOM element to exist
        if (isProbQuestion && !elementReference.current) {
            return;
        }

        // Check if this is a valid question element that should receive focus
        const isQuestionElement =
            variable &&
            variable.id &&
            getQuestionId() &&
            elementReference.current &&
            !elementReference.current.hasAttribute("disabled") &&
            elementReference.current.getAttribute("tabindex") !== "-1";

        // Register the element if not already registered. If already registered,
        // ensure the stored elementRef points to this component's ref so it stays
        // valid after remounts / hide-unhide cycles.
        const currentElementStatus = getCurrentElementFromStore();
        if (currentElementStatus) {
            // Update ref if identity changed (component remount created a new ref)
            if (currentElementStatus.elementRef !== elementReference) {
                currentElementStatus.elementRef = elementReference;
                // push the update to Redux so navigation code uses the fresh ref
                dispatch(setDefaultFocusStatus({ ...defaultFocusStatus }));
            }
        }

        if (!currentElementStatus) {
            if (isProbQuestion) {
                const aiProbeParentIndex = defaultFocusStatus.statuses.findIndex(
                    (status) => status.questionId === question.id,
                );
                if (aiProbeParentIndex !== -1) {
                    const newElementStatus = {
                        elementId,
                        elementRef: elementReference,
                        variableId: variable.id,
                        questionId: getQuestionId(),
                        focusStatus: true,
                        isQuestionElement: false,
                    };
                    defaultFocusStatus.statuses.forEach((status) => {
                        if (status.focusStatus && status.elementId.includes("_AI_PROBE")) {
                            status.focusStatus = false;
                        }
                    });
                    elementReference.current.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                    elementReference.current.focus();
                    let insertIndex = aiProbeParentIndex + 1;
                    while (
                        insertIndex < defaultFocusStatus.statuses.length &&
                        defaultFocusStatus.statuses[insertIndex].elementId.includes("_AI_PROBE")
                    ) {
                        insertIndex++;
                    }
                    defaultFocusStatus.statuses.splice(insertIndex, 0, newElementStatus);
                    dispatch(setDefaultFocusStatus({ ...defaultFocusStatus }));
                }
            } else {
                // For regular elements, we can register even without DOM element
                defaultFocusStatus.statuses.push({
                    elementId,
                    elementRef: elementReference,
                    variableId: variable.id,
                    questionId: getQuestionId(),
                    rowIndex,
                    focusStatus: false,
                    isQuestionElement: elementReference.current ? isQuestionElement : false,
                });
                dispatch(setDefaultFocusStatus({ ...defaultFocusStatus }));
            }
        }

        // Only handle auto-focus if DOM element exists
        if (!elementReference.current) {
            return;
        }

        // Only handle focus if this is a question element, autofocus is requested, and we're
        // embedded inside Voxco Agent (not any arbitrary iframe embedder).
        const isVoxcoAgent = checkIfS2LoadedInsideAgent(parent);
        if (isQuestionElement && shouldAutoFocus && isVoxcoAgent) {
            const firstQuestionElement = defaultFocusStatus.statuses.find(
                (status) => status.isQuestionElement,
            );

            const isFirstQuestion =
                firstQuestionElement &&
                firstQuestionElement.elementId === elementId &&
                firstQuestionElement.variableId === variable.id;

            if (isFirstQuestion) {
                // Reset all focus statuses and focus first question
                defaultFocusStatus.statuses.forEach((status) => (status.focusStatus = false));
                elementReference.current.focus();
                elementReference.current.scrollIntoView({ behavior: "smooth", block: "center" });

                // Update focus status for this element
                const updatedStatus = getCurrentElementFromStore();
                if (updatedStatus) {
                    const index = defaultFocusStatus.statuses.indexOf(updatedStatus);
                    defaultFocusStatus.statuses[index].focusStatus = true;
                    dispatch(setDefaultFocusStatus({ ...defaultFocusStatus }));
                }
            }
        }
    }

    // Handle visibility changes
    useEffect(() => {
        function checkAgentVisibility() {
            const isVoxcoAgent = checkIfS2LoadedInsideAgent(parent);

            if (isVoxcoAgent) {
                const observer = new IntersectionObserver(
                    (entries) => {
                        entries.forEach((entry) => {
                            if (entry.isIntersecting) {
                                const computedStyle = window.getComputedStyle(
                                    document.documentElement,
                                );
                                const isVisible =
                                    computedStyle.display !== "none" &&
                                    computedStyle.visibility !== "hidden";

                                if (isVisible) {
                                    handleFocus(true);
                                }
                            }
                        });
                    },
                    { threshold: 0 },
                );

                observer.observe(document.documentElement);
                return () => observer.disconnect();
            }
        }

        const cleanup = checkAgentVisibility();
        return () => {
            if (cleanup) cleanup();
        };
    }, [parent]);

    // Handle initial mount
    useEffect(() => {
        if (variable && defaultFocusStatus && defaultFocusStatus.statuses) {
            const shouldAutoFocus = !defaultFocusStatus.statuses.some(
                (status) => status.focusStatus,
            );
            handleFocus(shouldAutoFocus);
        }
    }, []);

    // Carousel statement switches reuse choice components (stable React keys) while elementId
    // and variableId change in the focus store. Re-sync refs after those updates.
    //
    // defaultFocusStatus is intentionally in deps: CarrouselControl relabels store entries in a
    // sibling effect that may run after this component receives new elementId/variable props, so
    // we retry on each focus-store notification until the relabeled entry exists. Each mounted
    // hook may dispatch once while refs are out of sync; the elementRef identity check converges
    // in one pass (typical carousel scales have ≤11 choices, so fan-out is negligible).
    useEffect(() => {
        if (!variable || !defaultFocusStatus?.statuses) {
            return;
        }

        const registered = defaultFocusStatus.statuses.find(
            (el) => el.variableId === variable.id && el.elementId === elementId,
        );
        if (registered && registered.elementRef !== elementReference) {
            registered.elementRef = elementReference;
            dispatch(setDefaultFocusStatus({ ...defaultFocusStatus }));
        }
    }, [elementId, variable?.id, defaultFocusStatus, dispatch]);

    function onKeyDown(
        event: React.KeyboardEvent<
            HTMLDivElement | HTMLTextAreaElement | HTMLButtonElement | Element
        >,
    ) {
        if (event.target !== event.currentTarget) return;

        if (event.key === " " && onSelect) {
            onSelect();
            event.preventDefault();

            // Set the focus to current element even in case of element selection as well
            const currentElementInfo = getCurrentElementFromStore();
            if (!variable || !currentElementInfo) return;
            const index = defaultFocusStatus.statuses.indexOf(currentElementInfo);
            if (index < 0) return;
            defaultFocusStatus.statuses[index].focusStatus = true;
            dispatch(setDefaultFocusStatus(defaultFocusStatus));
        } else {
            const currentElementInfo = getCurrentElementFromStore();
            if (currentElementInfo) {
                navigation.handleKeyPressEvent(event, currentElementInfo);
            }
        }
    }

    return { elementReference, onKeyDown };
}
